import {
  Regulation,
  StandstillExemption,
  type SentStatutoryClausesDto,
} from "../../../autogen/bff-api";
import { displayOsloDate } from "../../../common/formatting/displayOsloDate";

// Standard Norwegian award-letter (tildelingsbrev) boilerplate. These are formal procurement-law
// texts rendered verbatim in the letters, so they are kept as Norwegian constants rather than i18n keys.

const senderOrFallback = (senderName?: string | null): string =>
  senderName?.trim() || "oppdragsgiver";

const contestChoice = (juryOnlyAdvises: boolean): string =>
  juryOnlyAdvises ? "oppdragsgiver, etter juryens vurdering, har valgt" : "juryen har kåret";

const prizeSentence = (prizes: string[]): string =>
  prizes.length === 0
    ? ""
    : prizes.length === 1
      ? `\n\nPremien er ${prizes[0]}.`
      : `\n\nPremiene i konkurransen er ${winnerNamesText(prizes)}.`;

export const winnerIntro = (
  eventTitle: string,
  senderName?: string | null,
  designContest = false,
  juryOnlyAdvises = false,
  designContestPrizes: string[] = [],
): string =>
  designContest
    ? `Vi viser til Deres bidrag i plan- og designkonkurransen ${eventTitle}.\n\n` +
      `På vegne av ${senderOrFallback(senderName)} meddeles herved at ${contestChoice(juryOnlyAdvises)} ` +
      "Deres bidrag som vinner." +
      prizeSentence(designContestPrizes)
    : `Vi viser til Deres tilbud i konkurransen om ${eventTitle}.\n\n` +
      `På vegne av ${senderOrFallback(senderName)} meddeles herved at kontrakten vil bli inngått med Deres firma.`;

export const rejectionIntro = (
  eventTitle: string,
  winnerName: string,
  senderName?: string | null,
  designContest = false,
  juryOnlyAdvises = false,
): string =>
  designContest
    ? `Vi viser til Deres bidrag i plan- og designkonkurransen ${eventTitle}.\n\n` +
      `På vegne av ${senderOrFallback(senderName)} meddeles herved at ${contestChoice(juryOnlyAdvises)} ` +
      `${winnerName.trim() ? `bidraget fra ${winnerName.trim()}` : "et annet bidrag"} som vinner.`
    : `Vi viser til Deres tilbud i konkurransen om ${eventTitle}.\n\n` +
      `På vegne av ${senderOrFallback(senderName)} meddeles herved at kontrakten vil bli inngått med ` +
      `${winnerName.trim() || "en annen leverandør"}.`;

export const signOff = (
  senderName?: string | null,
  signerName?: string | null,
): string =>
  ["Med vennlig hilsen,", signerName?.trim(), senderOrFallback(senderName)]
    .filter(Boolean)
    .join("\n");

export const winnerNamesText = (names: string[]): string =>
  names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} og ${names[names.length - 1]}`;

export interface StandstillTerms {
  buyerSetsStandstill: boolean;
  days?: number | null;
  exemption?: StandstillExemption | null;
  rejectedInFavourOf?: string | null;
  expiresAt?: string | null;
  awaitsDispatch?: boolean;
  voluntary?: boolean;
}

const soleConcernedSupplier = (rejectedInFavourOf?: string | null): string =>
  rejectedInFavourOf?.trim()
    ? `Det er ingen karensperiode fordi ${rejectedInFavourOf.trim()} er den eneste berørte leverandøren etter at ` +
      "de øvrige tilbudene er avvist (FOA § 10-2 tredje ledd bokstav b)."
    : "Det er ingen karensperiode fordi dere er den eneste berørte leverandøren (FOA § 10-2 tredje ledd bokstav b).";

const NO_STANDSTILL: Record<
  StandstillExemption.FrameworkAgreementCallOff,
  string
> = {
  [StandstillExemption.FrameworkAgreementCallOff]:
    "Det er ingen karensperiode fordi kontrakten tildeles under en rammeavtale " +
    "(FOA § 10-2 tredje ledd bokstav c).",
};

const noticeExemption = (regulation: Regulation | null | undefined): string =>
  regulation === Regulation.Forsyningsforskriften
    ? "forsyningsforskriften § 21-2 annet ledd bokstav a"
    : "FOA § 25-2 annet ledd bokstav a";

const withoutPriorNotice = (
  regulation: Regulation | null | undefined,
): string =>
  `Det er ingen karensperiode fordi kontrakten inngås uten forutgående kunngjøring (${noticeExemption(regulation)}).`;

const CONTRACT_WAITS =
  "Kontrakten vil ikke bli inngått før karensperioden er utløpt.";

const expirySentence = ({
  expiresAt,
  awaitsDispatch,
}: StandstillTerms): string | null => {
  if (expiresAt)
    return `Karensperioden utløper ${displayOsloDate(expiresAt, "nb")}.`;
  if (awaitsDispatch)
    return "Datoen karensperioden utløper, fastsettes når meddelelsen sendes.";
  return null;
};

const statutoryKarensInfo = (
  provision: string,
  standstill: StandstillTerms,
): string => {
  const { days } = standstill;
  const period =
    days == null
      ? "en karensperiode"
      : `en karensperiode på ${days} ${days === 1 ? "dag" : "dager"}`;
  return [
    `Det gjelder ${period} regnet fra dagen etter at denne meddelelsen er sendt, jf. ${provision}.`,
    expirySentence(standstill),
    CONTRACT_WAITS,
  ]
    .filter(Boolean)
    .join(" ");
};

const voluntaryKarensInfo = (
  regulation: Regulation | null | undefined,
  standstill: StandstillTerms,
): string => {
  const days = standstill.days ?? 0;
  return [
    `Oppdragsgiveren har valgt å praktisere en karensperiode på ${days} ${days === 1 ? "dag" : "dager"} regnet fra ` +
      "dagen etter at denne meddelelsen er sendt, selv om karensperiode ikke er påkrevd " +
      `(${noticeExemption(regulation)}).`,
    expirySentence(standstill),
    CONTRACT_WAITS,
  ]
    .filter(Boolean)
    .join(" ");
};

const buyerSetKarensInfo = (standstill: StandstillTerms): string => {
  const { exemption, rejectedInFavourOf } = standstill;
  if (exemption === StandstillExemption.SoleConcernedSupplier)
    return soleConcernedSupplier(rejectedInFavourOf);
  if (exemption) return NO_STANDSTILL[exemption];
  return statutoryKarensInfo("anskaffelsesforskriften § 10-2", standstill);
};

export const karensInfo = (
  regulation: Regulation | null | undefined,
  standstill?: StandstillTerms,
): string => {
  if (standstill?.voluntary) {
    return (standstill.days ?? 0) > 0
      ? voluntaryKarensInfo(regulation, standstill)
      : withoutPriorNotice(regulation);
  }
  if (standstill?.buyerSetsStandstill) return buyerSetKarensInfo(standstill);
  const provision =
    regulation === Regulation.Forsyningsforskriften
      ? "forsyningsforskriften § 21-2"
      : "anskaffelsesforskriften § 25-2";
  return statutoryKarensInfo(
    provision,
    standstill ?? { buyerSetsStandstill: false },
  );
};

export const KLAGE_INFO =
  "Eventuell klage på tildelingsbeslutningen kan rettes til oppdragsgiver eller til Klagenemnda for " +
  "offentlige anskaffelser (KOFA), Postboks 511 Sentrum, 5805 Bergen, eller per e-post post@kofa.no.";

const CONTEST_KLAGE_INFO =
  "Eventuell klage på utfallet av konkurransen kan rettes til oppdragsgiver eller til Klagenemnda for " +
  "offentlige anskaffelser (KOFA), Postboks 511 Sentrum, 5805 Bergen, eller per e-post post@kofa.no.";

const CONTEST_NO_STANDSTILL = "Plan- og designkonkurransen tildeler ingen kontrakt, så det gjelder ingen karensperiode.";

export interface StatutoryClauseContext {
  eventRunsPrivateFlow: boolean;
  buyerRunsPublicProcurements: boolean;
  outsideProcurementAct?: boolean;
  regulation: Regulation | null | undefined;
  standstill?: StandstillTerms;
  designContest?: boolean;
  juryOnlyAdvises?: boolean;
  designContestPrizes?: string[];
}

// FOA § 25-2 standstill never applies to the private flow (private buyer or FOA Part I). KOFA hears only
// public buyers (LOA § 7a), and nothing under 500 000 kr (LOA § 2, klagenemndforskriften § 6).
export const statutoryClauses = ({
  eventRunsPrivateFlow,
  buyerRunsPublicProcurements,
  outsideProcurementAct = false,
  regulation,
  standstill,
  designContest = false,
}: StatutoryClauseContext): { standstill?: string; complaint?: string } => ({
  ...(eventRunsPrivateFlow
    ? {}
    : { standstill: designContest ? CONTEST_NO_STANDSTILL : karensInfo(regulation, standstill) }),
  ...(buyerRunsPublicProcurements && !outsideProcurementAct
    ? { complaint: designContest ? CONTEST_KLAGE_INFO : KLAGE_INFO }
    : {}),
});

export const clausesAsShown = (
  context: StatutoryClauseContext,
  sentClauses?: SentStatutoryClausesDto | null,
): { standstill?: string; complaint?: string } =>
  sentClauses
    ? {
        standstill: sentClauses.standstill ?? undefined,
        complaint: sentClauses.complaint ?? undefined,
      }
    : statutoryClauses(context);

export const warnsAgainstNamingWinner = (
  isRejection: boolean,
  namesWinnerToOtherBidders: boolean,
): boolean => isRejection && !namesWinnerToOtherBidders;

export const withdrawnStandstill = (
  buyerSetsStandstill: boolean,
  voluntary: boolean,
  decisionDays: number | null | undefined,
): StandstillTerms =>
  voluntary
    ? { buyerSetsStandstill, voluntary, days: decisionDays }
    : { buyerSetsStandstill };

export const forRecipient = (
  context: StatutoryClauseContext,
  rejectedInFavourOf: string | null,
): StatutoryClauseContext =>
  context.standstill
    ? { ...context, standstill: { ...context.standstill, rejectedInFavourOf } }
    : context;
